import { useEffect, useState } from "react";

import { useAddConfigSettings } from "@/api/chat-settings/add-config-settings";
import { useGetWidgetSettings } from "@/api/chat-settings/get-widget-settings";
import { useUpdateConfigSettings } from "@/api/chat-settings/update-config-settings";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Link } from "react-router";

const Configuration = () => {
    const [config, setConfig] = useState({
        isOrderTrack: false,
        orderFormat: "",
        isForwardRequest: false,
        forwardRequestEmail: "",
        forwardEmailInfo: "",
    });

    const valueChangeHandler = (key: string, value: string | boolean) => {
        setConfig((prev) => ({ ...prev, [key]: value }));
    };

    const { data: chatSettings, isLoading } = useGetWidgetSettings();
    const { mutate: addConfig, isPending: addPending } = useAddConfigSettings();
    const { mutate: updateConfig, isPending: updatePending } = useUpdateConfigSettings();

    const saveHandler = () => {
        if (chatSettings?.data?.config) {
            updateConfig({
                forward_email_address: config.forwardRequestEmail,
                forward_email_auto_reply_text: config.forwardEmailInfo,
                is_forward_email: config.isForwardRequest,
                order_format_example: config.orderFormat,
                order_tracking_status: config.isOrderTrack,
            });
        }

        if (!chatSettings?.data?.config) {
            addConfig({
                forward_email_address: config.forwardRequestEmail,
                forward_email_auto_reply_text: config.forwardEmailInfo,
                is_forward_email: config.isForwardRequest,
                order_format_example: config.orderFormat,
                order_tracking_status: config.isOrderTrack,
            });
        }
    };

    useEffect(() => {
        if (chatSettings?.data?.config) {
            setConfig({
                isOrderTrack: chatSettings?.data?.config?.order_tracking_status,
                orderFormat: chatSettings?.data?.config?.order_format_example,
                isForwardRequest: chatSettings?.data?.config?.is_forward_email,
                forwardRequestEmail: chatSettings?.data?.config?.forward_email_address,
                forwardEmailInfo: chatSettings?.data?.config?.forward_email_auto_reply_text,
            });
        }
    }, [chatSettings]);

    return (
        <div>
            {isLoading || addPending || updatePending ? (
                <Skeleton className="h-48 w-full rounded-lg" />
            ) : (
                <Card className="p-4 text-gray-700 shadow-none">
                    <div className="flex items-center justify-between">
                        <div className="flex gap-10">
                            <h3 className="text-lg font-bold">Order Tracking</h3>
                            {chatSettings?.data?.config?.order_tracking_status ? (
                                <p>
                                    Status: <span className="text-green-600">Active</span>
                                </p>
                            ) : (
                                <p>
                                    Status: <span className="text-red-600">Inactive</span>
                                </p>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <Label htmlFor="toggle_order_tracking">
                                {chatSettings?.data?.config?.order_tracking_status ? "Deactivate" : "Activate"}
                            </Label>
                            <Switch
                                id="toggle_order_tracking"
                                checked={config.isOrderTrack}
                                onCheckedChange={(value) => valueChangeHandler("isOrderTrack", value)}
                            />
                        </div>
                    </div>
                    <div>
                        <p>Allow customers to look up where their order is automatically Requires custom API endpoint integration.</p>
                        <Link to="#" className="font-semibold text-blue-600">
                            See Integrations
                        </Link>
                    </div>
                    <Separator className="my-4" />

                    <div>
                        <h3 className="text-lg font-bold">Order Format Example</h3>
                        <p className="mt-1 text-sm">An example of the order format the customer has to provide</p>
                        <Input
                            type="text"
                            placeholder="Enter Example"
                            className="mt-4 min-h-12"
                            value={config.orderFormat}
                            onChange={(e) => valueChangeHandler("orderFormat", e.target.value)}
                        />
                    </div>
                </Card>
            )}
            {isLoading || addPending || updatePending ? (
                <Skeleton className="mt-4 h-48 w-full rounded-lg" />
            ) : (
                <Card className="mt-4 p-4 text-gray-700 shadow-none">
                    <div className="flex items-center justify-between">
                        <div className="flex gap-10">
                            <h3 className="text-lg font-bold">Forward Request</h3>
                            {chatSettings?.data?.config?.is_forward_email ? (
                                <p>
                                    Status: <span className="text-green-600">Active</span>
                                </p>
                            ) : (
                                <p>
                                    Status: <span className="text-red-600">Inactive</span>
                                </p>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <Label htmlFor="toggle_order_tracking">{chatSettings?.data?.config?.is_forward_email ? "Deactivate" : "Activate"}</Label>
                            <Switch
                                id="toggle_order_tracking"
                                checked={config.isForwardRequest}
                                onCheckedChange={(value) => valueChangeHandler("isForwardRequest", value)}
                            />
                        </div>
                    </div>
                    <div>
                        <p>Allow Chatbot to forward emails to your support team when the customer needs help Only available with</p>
                        <p>
                            one of our higher plans.
                            <Link to="#" className="font-semibold text-blue-600">
                                Upgrade plan
                            </Link>
                        </p>
                    </div>
                    <Separator className="my-4" />

                    <div>
                        <h3 className="text-lg font-bold">Forward Email</h3>
                        <p className="mt-1 text-sm">The email where you wish to receive your forwarded requests</p>
                        <Input
                            type="text"
                            placeholder="Enter Email Address"
                            className="mt-4 min-h-12"
                            value={config.forwardRequestEmail}
                            onChange={(e) => valueChangeHandler("forwardRequestEmail", e.target.value)}
                        />
                    </div>
                </Card>
            )}
            {isLoading || addPending || updatePending ? (
                <Skeleton className="mt-4 h-48 w-full rounded-lg" />
            ) : (
                <Card className="mt-4 p-4 text-gray-700 shadow-none">
                    <div className="flex items-center justify-between">
                        <div className="flex gap-10">
                            <h3 className="text-lg font-bold">Contact Information</h3>
                        </div>
                    </div>
                    <Separator className="my-4" />

                    <div>
                        <h3 className="text-lg font-bold">Forward Email</h3>
                        <p className="mt-1 text-sm">If a visitor wants to get in touch, this is the info they&apos;ll receive</p>
                        <Textarea
                            rows={3}
                            placeholder="Contact Information"
                            className="mt-4 min-h-12"
                            value={config.forwardEmailInfo}
                            onChange={(e) => valueChangeHandler("forwardEmailInfo", e.target.value)}
                        />
                    </div>
                    <h3 className="mt-4 text-lg font-bold">Examples</h3>
                    <ul className="list-inside list-disc">
                        <li>If you want to get in touch, feel free to use our contact form at example.com/contact.</li>
                        <li>If you&apos;d like to connect, just give us a call at 01234567890</li>
                        <li>To reach out, simply drop us an email at contact@example.com.</li>
                        <li>For direct chat, join our Discord server at discord.gg/example.</li>
                        <li>You can also find us on Twitter @example if you&apos;d like to contact us.</li>
                    </ul>
                </Card>
            )}
            <Card className="mt-4 p-4 shadow-none">
                <div className="flex justify-end">
                    <Button onClick={saveHandler}>Save Changes</Button>
                </div>
            </Card>
        </div>
    );
};

export default Configuration;
